import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Grid,
  Box,
  TextField,
  Typography,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from '@material-ui/core';
import CardWitTitle from './CardWithTitle';
import { statusCodes } from '../utilities/constants';
import { decisionResponses } from '../data/decisionResponses';
import { getAppName } from '../utilities/getAppName';

const personnummer = require('personnummer.js');

function ApplicationDecision({ submission, usersWithSamePersonalId, onChange, isSaving, ...props }) {

  const [localStatus, setLocalStatus] = useState(null);
  const [localFeedback, setLocalFeedback] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const extra = useRef("");

  useEffect(() => {
    (async () => {
      extra.current = "";
      if (
        typeof decisionResponses[localStatus?.code]?.response?.extra === 'function'
        && typeof submission?.storeGroupRef !== 'undefined'
      ) {
        const fn = decisionResponses[localStatus.code].response.extra;
        extra.current = await fn(submission.storeGroupRef);
      }
    })();
  }, [localStatus?.code, submission?.storeGroupRef]);

  const generateEmailHtml = () => {
    let _localFeedback = localFeedback?.trim().replace(/[\r\n]/gm, "<br>");
    let email = "<p>Hej,</p>";
    email += `<p>Tack för din ansökan till ${getAppName()}.</p>`;
    email += `<p id="email-description">${decisionResponses[localStatus?.code]?.response?.heading}. ${decisionResponses[localStatus?.code]?.response?.text}</p>`;
    email += _localFeedback && `<p>${_localFeedback}</p>`;
    email += extra.current && `<p>${extra.current}</p>`;
    email += `<p>Hälsningar ${getAppName()}.</p>`;
    return email;
  };

  const handleFeedbackChange = event => {
    const name = event.target.name;
    let key = null;
    let value = event.target.value;
    switch (name) {
      case 'feedback-from-admin':
        key = 'feedbackFromAdmin';
        setLocalFeedback(value);
        break;
      default:
        break;
    }
    const _submission = {
      ...submission,
      [key]: value,
      newDecision: false,
    };
    onChange(_submission);
  };

  const handleStatusChange = event => {
    const statusCode = statusCodes[event.target.value];
    const _submission = {
      ...submission,
      statusCode: statusCode,
      feedbackFromAdmin: "",
      newDecision: false,
    };
    onChange(_submission);
    setLocalStatus(statusCode);
    setLocalFeedback("");
  };

  const handleAddReason = reason => {
    setLocalFeedback(prevLocalFeedback => (
      `${prevLocalFeedback}${reason.heading}. ${reason.text}\n\n`
    ));
  };

  const handleApproveEmail = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleSave = () => {
    const _submission = {
      ...submission,
      feedbackFromAdmin: localFeedback.trim(),
      emailHtml: generateEmailHtml(),
      newDecision: true,
    };
    onChange(_submission);
  };

  /** Not used at the moment. */
  // const validateForRejected = useCallback(() => {
  //   let validate = false;
  //   if (
  //     !submission?.approveIdentity
  //     || !submission?.approvePersonalInfo
  //     || !submission?.approveSupportingDocuments
  //   ) {
  //     validate = true;
  //   }
  //   return validate;
  // }, [submission, usersWithSamePersonalId]);

  /** Not used at the moment. */
  // const validateForUnresolved = useCallback(() => {
  //   let validate = false;
  //   if (
  //     !submission?.approveIdentity
  //     || !submission?.approvePersonalInfo
  //     || !submission?.approveSupportingDocuments
  //   ) {
  //     validate = true;
  //   }
  //   return validate;
  // }, [submission, usersWithSamePersonalId]);

  const validateForApproved = useCallback(() => {
    let canApprove = false;
    if (personnummer.validate(submission?.personalId)
      && submission?.approveIdentity
      && submission?.firstName?.length >= 2
      && submission?.lastName?.length >= 2
      && submission?.approvePersonalInfo
      && submission?.approveSupportingDocuments
      // && willResetAllUserDuplicates()
      && !isSaving) {
      canApprove = true;
    }
    return canApprove;
  }, [submission, usersWithSamePersonalId]);

  const willResetAllUserDuplicates = useCallback(() => {
    if (!usersWithSamePersonalId) { return false; }
    let allReset = true;
    for (let i = 0; i < usersWithSamePersonalId?.length; i++) {
      const user = usersWithSamePersonalId[i];
      if (user.reset === false) {
        allReset = false;
      }
    };
    return allReset;
  }, [usersWithSamePersonalId]);

  const useStyles = makeStyles(theme => ({
    radioGroup: {
      display: 'flex',
      flexDirection: 'row',
      alignContent: 'center',
    },
    radioReject: {
      color: theme.palette.reject.main,
      '&.Mui-checked': {
        color: theme.palette.reject.main,
      }
    },
    radioUnresolved: {
      color: theme.palette.unresolved.main,
      '&.Mui-checked': {
        color: theme.palette.unresolved.main,
      }
    },
    radioApprove: {
      color: theme.palette.primary.main,
      '&.Mui-checked': {
        color: theme.palette.primary.main,
      }
    },
  }));

  const classes = useStyles();

  return (
    <React.Fragment>
      <CardWitTitle title="Beslut">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RadioGroup
              className={classes.radioGroup}
              id='status-change'
              name='status-change'
              onChange={handleStatusChange}
            >
              <FormControlLabel
                value={statusCodes[400].code}
                control={<Radio className={classes.radioReject} />}
                label={<Typography style={{ marginRight: 50 }}>Neka</Typography>}
                // disabled={isSaving || !validateForRejected()}
                disabled={isSaving}
              />
              <FormControlLabel
                value={statusCodes[500].code}
                control={<Radio className={classes.radioUnresolved} />}
                label={<Typography style={{ marginRight: 45 }}>Komplettering krävs</Typography>}
                // disabled={isSaving || !validateForUnresolved()}
                disabled={isSaving}
              />
              <FormControlLabel
                value={statusCodes[600].code}
                control={<Radio className={classes.radioApprove} />}
                label={<Typography>Godkänn</Typography>}
                disabled={isSaving || !validateForApproved()}
              />
            </RadioGroup>
          </Grid>
          <Grid item xs={12}>
            {
              localStatus
              && decisionResponses[localStatus.code]
              && decisionResponses[localStatus.code].reasons?.map(reason =>
                <Button
                  key={reason.label}
                  onClick={() => handleAddReason(reason)}
                >{reason.label}
                </Button>
              )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              multiline
              minRows={20}
              onChange={handleFeedbackChange}
              // value={submission?.feedbackFromAdmin}
              value={localFeedback}
              id='feedback-from-admin'
              name='feedback-from-admin'
              label="Kommentar att skicka till kund"
              variant='outlined'
              fullWidth
              disabled={isSaving || !localStatus}
            />
          </Grid>
          <Grid item xs={12}>
            <Box display='flex' justifyContent='left' alignItems='center'>
              <Button
                variant='contained'
                color='primary'
                onClick={handleApproveEmail}
                disabled={isSaving || !localStatus}
              >Skicka...</Button>
            </Box>
          </Grid>
        </Grid>

      </CardWitTitle>

      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        scroll={'paper'}
        aria-labelledby="email"
        aria-describedby="email-description"
      >
        <DialogTitle id="email">Skicka beslut till kund?</DialogTitle>
        <DialogContent dividers={true} style={{ lineHeight: 'inherit' }}>
          <div dangerouslySetInnerHTML={{ __html: generateEmailHtml() }} />
        </DialogContent>
        <DialogActions>
          <Button
            color='primary'
            onClick={handleModalClose}
          >
            Avbryt
          </Button>
          <Button
            color='primary'
            onClick={handleSave}
            disabled={isSaving}
          >
            Skicka
          </Button>
        </DialogActions>
      </Dialog>

    </React.Fragment >
  );
}

export default ApplicationDecision;